<template>
  <v-card id="booking-card" class="max-shadow">
    <v-toolbar class="white">
      <v-toolbar-title class="py-2">
        <div class="title-2">Active Users</div>
      </v-toolbar-title>
      <v-spacer />

      <v-menu offset-y max-height="280">
        <template v-slot:activator="{ on }">
          <v-btn depressed v-on="on" color="orange--text darken-1">
            {{ filter.name }}
            <v-icon class="pl-2"> arrow_drop_down </v-icon>
          </v-btn>
        </template>
        <v-list dense>
          <v-list-tile @click="updateFilter('all', 'All')">
            <v-list-tile-title>All</v-list-tile-title>
          </v-list-tile>
          <v-divider />
          <v-list-tile
            v-for="(cid, index) in Object.keys(tenants)"
            :key="index"
            @click="updateFilter(cid, tenants[cid])"
          >
            <v-list-tile-title>{{ tenants[cid] }}</v-list-tile-title>
          </v-list-tile>
        </v-list>
      </v-menu>

      <v-menu offset-y>
        <template v-slot:activator="{ on }">
          <v-btn depressed v-on="on">
            {{ filterBy }}
            <v-icon class="pl-2"> arrow_drop_down </v-icon>
          </v-btn>
        </template>
        <v-list dense>
          <v-list-tile
            v-for="(date, index) in dateRange"
            :key="index"
            @click="updateDate(date)"
          >
            <v-list-tile-title>{{ date }}</v-list-tile-title>
          </v-list-tile>
        </v-list>
      </v-menu>
    </v-toolbar>

    <v-card px-1>
      <v-layout row class="pt-2 pb-1 pl-4" xl12>
        <v-card-title primary-title class="headline">
          {{ range }}
        </v-card-title>
        <v-spacer />
        <v-flex sm2 v-if="filter.cid != 'all'">
          <count-widget
            color="orange darken-2"
            icon="people"
            :refresh="false"
            title="Onboarded"
            :value="onboardUsers.onboard"
            style="max-width: 200px; margin: 0 auto"
          />
        </v-flex>
        <v-flex sm2 v-if="filter.cid != 'all'">
          <count-widget
            color="orange darken-2"
            title="Total Users"
            icon="people"
            :refresh="false"
            :value="onboardUsers.total"
            style="max-width: 200px; margin: 0 auto"
          />
        </v-flex>
        <v-flex xs6 sm4>
          <count-widget-ticket
            color="orange darken-2"
            icon="people"
            title="Average Users Count"
            :value="totalCount"
            style="max-width: 300px; margin: 0 auto"
          />
        </v-flex>
      </v-layout>
      <div class="px-3">
        <v-progress-linear
          :active="loading"
          :indeterminate="true"
          height="2"
          top
        />
      </div>
      <div>
        <v-card-text>
          <lineChart
            :chart-data="usersLog"
            :options="chartOptions"
            :height="chartHeight"
          />
        </v-card-text>
      </div>
    </v-card>

    <v-snackbar v-model="snackbar" :color="snackColor" :timeout="0" bottom>
      {{ snackbarMessage }}
      <v-btn flat @click.native="snackbar = false"> Close </v-btn>
    </v-snackbar>
  </v-card>
</template>

<script>
import * as moment from 'moment';
import { functions } from '@/firebase';
import TicketCountwidget from '@/components/common/TicketCountWidget';
import CountWidget from '@/components/common/CountWidget';
import _get from 'lodash/get';
import lineChart from '@/components/common/LineChart';
import { firestore } from '@/firebase';

export default {
  name: 'UserAnalytics',
  components: {
    'count-widget-ticket': TicketCountwidget,
    'count-widget': CountWidget,
    lineChart,
  },
  data() {
    return {
      accent: '#FF7700',
      loading: true,
      startAt: '',
      endAt: '',
      snackbar: false,
      snackColor: 'primary',
      snackbarMessage: '',
      dateRange: [
        'Today',
        'Yesterday',
        'This week',
        'This month',
        'Last 30 days',
        'Last 60 days',
        'Last 90 days',
        'This Year',
        'Last calendar year',
      ],
      filter: { cid: 'all', name: 'All' },
      onboardUsers: { total: 0, onboard: 0 },
      log: {},
      totalCount: 0,
      diff: 1,
      range: '',
      totalFacilities: 0,
      filterBy: '',
      usersLog: [],
      activeUsersLog: {},
      tenants: {},
      chartHeight: 180,
      chartOptions: {
        responsive: true,
        legend: false,
        scales: {
          yAxes: [
            {
              ticks: {
                beginAtZero: true,
                stepSize: 1,
                spanGaps: true,
              },
              gridLines: {
                display: false,
              },
            },
          ],
          xAxes: [
            {
              gridLines: {
                display: false,
              },
            },
          ],
        },
      },
      yStepSize: 0.25,
      firebaseListeners: [],
    };
  },
  created() {
    this.tenantsListener();
  },
  beforeDestroy() {
    // unsubscribe all firebase listeners
    this.firebaseListeners.forEach((listenerFunc) => {
      listenerFunc();
    });
  },
  computed: {
    role() {
      return _get(this.$store.state.profile, 'role');
    },
    cid() {
      return _get(this.$store.state.profile, 'cid');
    },
  },
  mounted() {
    this.updateDate(this.dateRange[0]);
  },
  methods: {
    tenantsListener() {
      const tenantsListener = firestore
        .collection('tenants')
        .onSnapshot((tenantsSnapshot) => {
          this.tenants = {};
          tenantsSnapshot.forEach((tenantSnapshot) => {
            const tenant = tenantSnapshot.data();
            tenant.id = tenantSnapshot.id;
            this.tenants[tenant.id] = tenant.companyName;
          });
        });
      this.firebaseListeners.push(tenantsListener);
    },
    fetchUserAnalytics() {
      this.loading = true;
      const fun = functions.httpsCallable('bigQuery-users-activeUsersList');
      fun({
        startAt: this.startAt,
        endAt: this.endAt,
        cid: this.filter.cid,
      })
        .then((result) => {
          const data = result.data || {};
          this.activeUsersLog = {};

          for (const log of data) {
            if (log.dates) {
              log.dates.forEach((date) => {
                if (this.activeUsersLog[date]) {
                  this.activeUsersLog[date].count += 1;
                } else {
                  this.activeUsersLog[date] = { count: 1 };
                }

                if (log.cid && log.cid.length > 0) {
                  if (this.activeUsersLog[date][log.cid[0]]) {
                    this.activeUsersLog[date][log.cid[0]] += 1;
                  } else {
                    this.activeUsersLog[date][log.cid[0]] = 1;
                  }
                }
              });
            }
          }

          this.loading = false;

          this.plotChart();

          if (Object.keys(this.activeUsersLog).length > 0) {
            var total = 0;
            Object.values(this.activeUsersLog).forEach((value) => {
              if (value.count) {
                total += value.count;
              }
            });
            this.totalCount = Math.round(total / this.diff);
          } else {
            this.totalCount = 0;
          }
        })
        .catch((err) => {
          console.error(err);
        });
    },
    fetchOnboardUserAnalytics() {
      this.onboardUsers = { total: 0, onboard: 0 };

      if (this.filter.cid == 'all') return;
      this.loading = true;
      const fun = functions.httpsCallable('bigQuery-users-onBoardedUsersList');
      fun({
        cid: this.filter.cid,
      })
        .then((result) => {
          this.onboardUsers = result.data;
          this.loading = false;
        })
        .catch((err) => {
          console.error(err);
          this.loading = false;
        });
    },
    plotChart() {
      var datasets = [
        {
          label: 'Users',
          backgroundColor: '#3366cc',
          borderColor: '#3366cc',
          fill: false,
          lineTension: 0,
          data: Object.values(this.activeUsersLog).map((date) => date.count),
        },
      ];
      if (this.filter.cid && this.filter.cid !== 'all') {
        datasets.push({
          label: this.filter.name,
          backgroundColor: '#FF7700',
          borderColor: '#FF7700',
          fill: false,
          lineTension: 0,
          data: Object.values(this.activeUsersLog).map(
            (val) => val[this.filter.cid] || 0
          ),
        });
      }

      this.usersLog = {
        datasets: datasets,
        labels: Object.keys(this.activeUsersLog).map((date) =>
          moment(date).format('DD MMM')
        ),
      };
    },
    fetchLog() {
      this.fetchUserAnalytics();
    },

    updateDate(date) {
      if (this.filterBy === date) return;

      this.filterBy = date;

      switch (this.filterBy) {
        case 'Today':
          this.startAt = moment().startOf('day').valueOf();
          this.endAt = moment().endOf('day').valueOf();

          break;

        case 'Yesterday':
          this.startAt = moment().subtract(1, 'days').startOf('day').valueOf();
          this.endAt = moment().subtract(1, 'days').endOf('day').valueOf();

          break;

        case 'This week':
          this.startAt = moment().startOf('week').valueOf();
          this.endAt = moment().endOf('day').valueOf();

          break;

        case 'This month':
          this.startAt = moment().startOf('month').valueOf();
          this.endAt = moment().endOf('day').valueOf();

          break;

        case 'Last 30 days':
          this.startAt = moment().subtract(29, 'days').startOf('day').valueOf();
          this.endAt = moment().endOf('day').valueOf();

          break;

        case 'Last 60 days':
          this.startAt = moment().subtract(59, 'days').startOf('day').valueOf();
          this.endAt = moment().endOf('day').valueOf();

          break;

        case 'Last 90 days':
          this.startAt = moment().subtract(89, 'days').startOf('day').valueOf();
          this.endAt = moment().endOf('day').valueOf();

          break;

        case 'This Year':
          this.startAt = moment().startOf('year').valueOf();
          this.endAt = moment().endOf('day').valueOf();

          break;

        case 'Last calendar year':
          this.startAt = moment().startOf('year').subtract(1, 'year').valueOf();
          this.endAt = moment().endOf('year').subtract(1, 'year').valueOf();
          break;

        default:
          break;
      }

      this.diff = moment(this.endAt).diff(moment(this.startAt), 'days');

      if (this.diff <= 1) {
        this.range = `${moment(this.startAt).format('DD MMM YYYY')}`;
      } else {
        this.range = `${moment(this.startAt).format('DD MMM YYYY')} - ${moment(
          this.endAt
        ).format('DD MMM YYYY')}`;
      }

      this.fetchLog();
    },
    updateFilter(cid, name) {
      if (!cid || this.filter.cid == cid) return;
      this.filter = { cid, name };

      this.plotChart();
      this.fetchOnboardUserAnalytics();
    },
  },
};
</script>

<style>
</style>
